import { JSX } from 'react';
import { Controller, FieldPath, UseFormReturn } from 'react-hook-form';

import { TrashIcon } from 'lucide-react';

import { ActionButton } from 'components/ActionButton';
import { Checkbox } from 'components/Checkbox';
import { Label } from 'components/Label';
import { Select } from 'components/Select';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';

import { PLS_DIMENSION_UNIT_OPTIONS, PLS_FIELD_LIMITS } from '../constants';
import { PlsFormValues } from '../types';
import { toSelectStringValue } from '../utils';

type CommodityItemInputCardProps = {
  formMethods: UseFormReturn<PlsFormValues>;
  index: number;
  onRemove: () => void;
  isDeletable: boolean;
};

export function CommodityItemInputCard({
  formMethods,
  index,
  onRemove,
  isDeletable,
}: CommodityItemInputCardProps): JSX.Element {
  const { control } = formMethods;

  return (
    <Flex
      direction='col'
      align='stretch'
      gap='sm'
      className='rounded-md border border-neutral-200 p-3'
    >
      <Flex justify='between' align='center' className='w-full'>
        <Typography variant='body-sm' weight='medium'>
          Commodity {index + 1}
        </Typography>
        {isDeletable && (
          <ActionButton
            onClick={onRemove}
            className='hover:bg-error-300 text-error-600'
            icon={TrashIcon}
            aria-label='Remove item'
          />
        )}
      </Flex>

      <RHFTextInput
        name={`commodities.${index}.description` as FieldPath<PlsFormValues>}
        label='Name'
        required
        maxLength={PLS_FIELD_LIMITS.commodityName}
      />

      <Grid cols='2' gap='sm' className='w-full m-0'>
        <RHFTextInput
          name={`commodities.${index}.weightTotal` as FieldPath<PlsFormValues>}
          label='Weight (lbs)'
          required
          inputType='number'
          options={{ required: 'Required', min: 0.01, valueAsNumber: true }}
        />
        <RHFTextInput
          name={`commodities.${index}.quantity` as FieldPath<PlsFormValues>}
          label='Total Pieces'
          required
          inputType='number'
          step={1}
          options={{ required: 'Required', min: 1, valueAsNumber: true }}
        />
      </Grid>

      <RHFTextInput
        name={
          `commodities.${index}.handlingQuantity` as FieldPath<PlsFormValues>
        }
        label='Pallets'
        inputType='number'
        step={1}
        options={{ min: 0, valueAsNumber: true }}
      />

      <div>
        <Label name={`commodities.${index}.dimensionUnit`} required>
          Unit of Measurement
        </Label>
        <Controller
          name={
            `commodities.${index}.dimensionUnit` as FieldPath<PlsFormValues>
          }
          control={control}
          rules={{ required: 'Required' }}
          render={({ field }) => (
            <Select
              name={field.name}
              className='h-8'
              placeholder='Choose'
              onChange={(value) => {
                field.onChange(toSelectStringValue(value));
              }}
              value={typeof field.value === 'string' ? field.value : undefined}
              options={PLS_DIMENSION_UNIT_OPTIONS.map((unit) => ({
                value: unit.value,
                label: unit.label,
              }))}
            />
          )}
        />
      </div>

      <Grid cols='3' gap='sm' className='w-full m-0'>
        <RHFTextInput
          name={`commodities.${index}.length` as FieldPath<PlsFormValues>}
          label='Length'
          inputType='number'
          labelClassName='text-xs'
          options={{ min: 0, valueAsNumber: true }}
        />
        <RHFTextInput
          name={`commodities.${index}.width` as FieldPath<PlsFormValues>}
          label='Width'
          inputType='number'
          labelClassName='text-xs'
          options={{ min: 0, valueAsNumber: true }}
        />
        <RHFTextInput
          name={`commodities.${index}.height` as FieldPath<PlsFormValues>}
          label='Height'
          inputType='number'
          labelClassName='text-xs'
          options={{ min: 0, valueAsNumber: true }}
        />
      </Grid>

      <Flex align='center' gap='lg'>
        <Controller
          name={
            `commodities.${index}.hazardousMaterial` as FieldPath<PlsFormValues>
          }
          control={control}
          render={({ field }) => (
            <Flex align='center' gap='sm'>
              <Checkbox
                id={`commodity-hazmat-${index}`}
                checked={Boolean(field.value)}
                onCheckedChange={(checked) => field.onChange(Boolean(checked))}
              />
              <Label name={`commodities.${index}.hazardousMaterial`}>
                Hazmat
              </Label>
            </Flex>
          )}
        />

        <Controller
          name={`commodities.${index}.overDimensional` as any}
          control={control}
          render={({ field }) => (
            <Flex align='center' gap='sm'>
              <Checkbox
                id={`commodity-over-dimensional-${index}`}
                checked={Boolean(field.value)}
                onCheckedChange={(checked) => field.onChange(Boolean(checked))}
              />
              <Label name={`commodities.${index}.overDimensional`}>
                Over-dimensional
              </Label>
            </Flex>
          )}
        />
      </Flex>
    </Flex>
  );
}
